import React, { useEffect, useState } from "react";
import { ChoiceButton, useShuffledChoices } from "../../grammar/shared";
import { ArabicLine, ChoiceLabel, Prompt, isArabicOnly } from "./shared";

/**
 * AudioMcItem — listen to a short Arabic line, pick its meaning. Auto-plays
 * on mount; when the learner has no audio seat or playback fails, the Arabic
 * text is shown instead so the item stays answerable (same fallback as the
 * grammar lessons' audio_mc).
 */
export default function AudioMcItem({ question, onAnswer, audio }) {
  const { data } = question;
  const [picked, setPicked] = useState(null);
  const [showText, setShowText] = useState(!audio.audioAllowed);
  const view = useShuffledChoices(data.choices, data.correct_index ?? 0, [question.id]);

  useEffect(() => {
    if (!audio.audioAllowed) return;
    Promise.resolve(audio.play(data.audio_text)).then((ok) => ok === false && setShowText(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);

  const pick = (idx) => {
    if (picked !== null) return;
    setPicked(idx);
    onAnswer({ correct: idx === view.correctIndex, answer: view.choices[idx] });
  };

  return (
    <>
      <Prompt>{question.prompt_en}</Prompt>
      {audio.audioAllowed && (
        <div className="flex justify-center mb-3">
          <button
            type="button"
            onClick={() => audio.play(data.audio_text)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-[filter] hover:brightness-110"
            style={{ background: "var(--violet-bg)", color: "var(--tag-violet)", border: "1px solid var(--panel-line)" }}
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7L8 5z" /></svg>
            {audio.playingText ? "Playing…" : "Play again"}
          </button>
        </div>
      )}
      {(showText || picked !== null) && <ArabicLine text={data.audio_text} size="text-3xl" />}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-w-xl mx-auto mt-2">
        {view.choices.map((choice, idx) => {
          let state = "idle";
          if (picked !== null) {
            if (idx === view.correctIndex) state = "correct";
            else if (idx === picked) state = "wrong";
            else state = "dim";
          }
          return (
            <ChoiceButton key={idx} arabic={isArabicOnly(choice)} state={state} disabled={picked !== null} onClick={() => pick(idx)}>
              <ChoiceLabel text={choice} />
            </ChoiceButton>
          );
        })}
      </div>
    </>
  );
}
